/*
 * Project: bellcloud
 * File: apps/frontend/src/components/device/connectDevice/ConnectionButtons.tsx
 * Description: Connection action buttons (Bluetooth/Cradle connect/disconnect)
 * Uses global connection store and reuses logic from useConnectionActions hook
 */

import React from 'react';
import styled from '@emotion/styled';
import { useConnectionStore } from '../../../store/connectionStore';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';
import { Button } from '@/components/ui';
import type { UiConnectedConnectionType } from '@/types/connection';

interface ConnectionButtonsProps {
  /** Enable cradle connection - optional, defaults to true */
  cradleAble?: boolean;
  /** Callback when device is loaded */
  onDeviceLoaded?: (deviceInfo: any, deviceConfig: any) => void;
  /** Callback when connection type changes */
  onConnectionChange?: (connectionType: UiConnectedConnectionType) => void;
  /** Callback when device is disconnected */
  onDisconnected?: () => void;
}

/**
 * ConnectionButtons Component
 *
 * Displays connection action buttons based on current state:
 * - Left: Bluetooth buttons
 * - Right: Cradle/Serial buttons (disabled if cradleAble is false)
 * - Automatically handles connect/disconnect actions
 * - Supports callbacks to notify parent components
 */
export const ConnectionButtons: React.FC<ConnectionButtonsProps> = ({
  cradleAble = true,
  onDeviceLoaded,
  onConnectionChange,
  onDisconnected,
}) => {
  const { protocol, adapter } = useConnectionStore();
  const {
    handleBluetoothConnect,
    handleCradleConnect,
    handleDisconnect,
    handleLoadDevice,
    refreshDeviceInfo,
    // handleScanAnother, // 잠시 제거 (Reload Device로 대체)
  } = useConnectionActions(onDeviceLoaded, onConnectionChange, onDisconnected);

  const isBluetoothConnected = protocol.type === 'ble';
  const isCradleConnected = adapter.type === 'cradle' || adapter.type === 'dock';

  // 현재 연결로 기기 정보를 다시 읽고, 폼이 저장된 값으로 리셋되도록 onDeviceLoaded까지 호출.
  // (refreshDeviceInfo는 store만 갱신하므로 콜백을 추가로 전달해야 편집 중인 폼이 되돌아간다)
  const handleReloadDevice = async () => {
    const result = await refreshDeviceInfo();
    if (result?.success && result.data && onDeviceLoaded) {
      onDeviceLoaded(result.data.deviceInfo, result.data.deviceConfig);
    }
  };

  return (
    <Container>
      {/* Left: Bluetooth Buttons */}
      <ButtonGroup>
        {!isBluetoothConnected ? (
          <Button
            variant={isCradleConnected ? 'tertiaryNeutral' : 'tertiary'}
            onClick={handleBluetoothConnect}
            disabled={isCradleConnected}
            width="100%"
          >
            Connect via Bluetooth
          </Button>
        ) : (
          <>
            <Button variant="tertiary" onClick={handleDisconnect} width="100%">
              Disconnect Bluetooth
            </Button>
            <Button variant="tertiary" onClick={handleReloadDevice} width="100%">
              Reload Device
            </Button>
            {/* 잠시 제거 (필요 시 복구)
            <Button variant="tertiary" onClick={handleScanAnother} width="100%">
              Scan Another Device
            </Button>
            */}
          </>
        )}
      </ButtonGroup>

      {/* Right: Cradle Buttons (disabled if cradleAble is false) */}
      <ButtonGroup>
        {!isCradleConnected ? (
          <Button
            variant={!cradleAble || isBluetoothConnected ? 'tertiaryNeutral' : 'tertiary'}
            onClick={handleCradleConnect}
            disabled={!cradleAble || isBluetoothConnected}
            width="100%"
          >
            Connect via Cradle
          </Button>
        ) : (
          <>
            {/* Disconnect is always enabled — user must be able to disconnect regardless of cradleAble */}
            <Button
              variant="tertiary"
              onClick={handleDisconnect}
              width="100%"
            >
              Disconnect Cradle
            </Button>
            <Button
              variant={!cradleAble ? 'tertiaryNeutral' : 'tertiary'}
              onClick={handleLoadDevice}
              disabled={!cradleAble}
              width="100%"
            >
              Load Device
            </Button>
          </>
        )}
      </ButtonGroup>
    </Container>
  );
};

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]}px;
  align-items: flex-start;
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]}px;
  flex: 1 1 50%;
  width: 50%;
`;
